/** Page building blocks shared by the sidebar, Home, the Projects hub and the editor: icons, tree rows, list rows. */
import type { PageKind } from "@bundle/contracts";
import type { PageTreeRow } from "@bundle/core";
import { iconGlyphs, type IconName } from "@bundle/tokens";
import { useState } from "react";
import { Icon, IconButton, ListRow, Menu, Pressable, SectionHeader, Spacer, Stack, Text, TextField, type MenuItem } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";

/** A page's icon: an emoji (content), an icon-font name ("icon:board"), or the default for its kind. */
export function PageIcon({ icon, kind, size = "icon.sm" }: { icon?: string; kind: PageKind; size?: "icon.sm" | "icon.md" | "icon.lg" }) {
  if (icon !== undefined && icon.startsWith("icon:")) {
    const name = icon.slice(5);
    if (name in iconGlyphs) return <Icon name={name as IconName} size={size} color="text.secondary" />;
  } else if (icon !== undefined && icon !== "") {
    return (
      <Text variant={size === "icon.lg" ? "title1" : size === "icon.md" ? "callout" : "footnote"} lines={1}>
        {icon}
      </Text>
    );
  }
  return <Icon name={kind === "database" ? "board" : "page"} size={size} color="text.tertiary" />;
}

/** Header of a sidebar docs section (Favorites / Workspace / Private) with an optional "+" template menu. */
export function DocsSectionHeader(p: {
  title: string;
  icon: IconName;
  collapsed: boolean;
  onToggle: () => void;
  addMenu?: MenuItem[];
  addLabel?: string;
  testID: string;
}) {
  return (
    <SectionHeader
      title={p.title}
      icon={p.icon}
      collapsed={p.collapsed}
      onToggle={p.onToggle}
      trailing={
        p.addMenu === undefined ? undefined : (
          <Menu items={p.addMenu} align="end" testID={`${p.testID}-add-menu`}>
            <IconButton icon="plus" size="sm" accessibilityLabel={p.addLabel ?? p.title} onPress={() => {}} testID={`${p.testID}-add`} />
          </Menu>
        )
      }
      testID={p.testID}
    />
  );
}

export type SidebarPageRowProps = {
  row: PageTreeRow;
  selected: boolean;
  onPress: () => void;
  onToggle: () => void;
  /** Row actions (also the right-click menu). */
  menu: MenuItem[];
  /** "+" on hover: a new subpage. */
  onAddSubpage?: () => void;
  /** Inline rename in progress. */
  renaming?: boolean;
  onRename?: (title: string) => void;
  onCancelRename?: () => void;
  /** Show the sharing badge (published / shared / only people invited) after the title (featureFlags.sharing). */
  showBadge?: boolean;
};

const BADGE_ICONS = { public: "globe", shared: "users", restricted: "lock" } as const;

/** One row of the page tree: disclosure, icon, title, and hover actions (… menu, + subpage). */
export function SidebarPageRow(p: SidebarPageRowProps) {
  const t = useT();
  const r = p.row;
  const [draft, setDraft] = useState(r.title);
  const indent = Array.from({ length: r.depth }, (_, i) => <Spacer key={i} size="4" />);
  if (p.renaming === true) {
    return (
      <Stack direction="horizontal" align="center" gap="1" paddingX="1" testID={`sidebar-page-rename-${r.key}`}>
        {indent}
        <Spacer size="5" />
        <TextField
          value={draft}
          onChange={setDraft}
          placeholder={t("docs.rename.placeholder")}
          autoFocus
          variant="outlined"
          onSubmit={() => p.onRename?.(draft.trim())}
          onCancel={() => p.onCancelRename?.()}
          testID={`sidebar-page-rename-input-${r.id}`}
        />
      </Stack>
    );
  }
  return (
    <ListRow
      title={r.title === "" ? t("page.untitled") : r.title}
      leading={<PageIcon icon={r.icon} kind={r.kind} />}
      tree={{ depth: r.depth, expanded: r.expandable ? r.expanded : undefined, onToggle: p.onToggle, toggleLabel: r.expanded ? t("docs.row.collapse") : t("docs.row.expand") }}
      trailing={
        p.showBadge === true && r.badge !== undefined ? (
          <Stack testID={`sidebar-page-badge-${r.key}`}>
            <Icon name={BADGE_ICONS[r.badge]} size="icon.sm" color="text.tertiary" />
          </Stack>
        ) : undefined
      }
      actions={
        <>
          <Menu items={p.menu} align="start" testID={`sidebar-page-menu-${r.key}`}>
            <IconButton icon="more" size="sm" accessibilityLabel={t("docs.menu.more")} onPress={() => {}} testID={`sidebar-page-more-${r.key}`} />
          </Menu>
          {p.onAddSubpage === undefined ? null : (
            <IconButton icon="plus" size="sm" accessibilityLabel={t("docs.menu.newSubpage")} onPress={p.onAddSubpage} testID={`sidebar-page-add-${r.key}`} />
          )}
        </>
      }
      current={p.selected}
      onPress={p.onPress}
      contextMenu={p.menu}
      testID={`sidebar-page-${r.key}`}
    />
  );
}

/** A page in a list (Home "Recent docs", the Projects hub). */
export function PageListRow(p: { title: string; icon?: string; kind: PageKind; subtitle?: string; onPress: () => void; menu?: MenuItem[]; testID: string }) {
  const t = useT();
  return (
    <Pressable direction="horizontal" radius="lg" onPress={p.onPress} contextMenu={p.menu} testID={p.testID}>
      <Stack grow direction="horizontal" align="center" gap="3" paddingX="3" paddingY="2.5">
        <Stack padding="2" radius="md" background="bg.surface" align="center" justify="center">
          <PageIcon icon={p.icon} kind={p.kind} size="icon.sm" />
        </Stack>
        <Stack grow gap="0.5">
          <Text weight="medium" lines={1}>
            {p.title === "" ? t("page.untitled") : p.title}
          </Text>
          {p.subtitle === undefined ? null : (
            <Text variant="footnote" color="text.secondary" lines={1}>
              {p.subtitle}
            </Text>
          )}
        </Stack>
        <Icon name="chevronRight" size="icon.sm" color="text.tertiary" />
      </Stack>
    </Pressable>
  );
}
